import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { get, patch } from '../../utils/apiClient';
import { SectionSkeleton } from '../../components/loading/ContentSkeleton';
import { clinicalLensOptions } from '../clinical-lenses/ClinicalLensSelector';

type ResponseStyle = 'supportive' | 'balanced' | 'challenging';
type CommunicationStyle = 'warm' | 'balanced' | 'direct';

interface AIInteractionPreferences {
  response_style: ResponseStyle;
  communication_style: CommunicationStyle;
  clinical_lenses: string[];
  default_clinical_lenses: string[];
  clinical_user: boolean;
}

const defaultPreferences: AIInteractionPreferences = {
  response_style: 'balanced',
  communication_style: 'balanced',
  clinical_lenses: [],
  default_clinical_lenses: [],
  clinical_user: false,
};

const responseOptions: Array<{
  value: ResponseStyle;
  label: string;
  description: string;
}> = [
  {
    value: 'supportive',
    label: 'Supportive',
    description: 'More encouragement and acknowledgment',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Concise, thoughtful, and neutral',
  },
  {
    value: 'challenging',
    label: 'Challenging',
    description: 'More likely to question assumptions and push your thinking',
  },
];

const communicationOptions: Array<{
  value: CommunicationStyle;
  label: string;
  description: string;
}> = [
  {
    value: 'warm',
    label: 'Warm & conversational',
    description: 'Friendly language without unnecessary praise',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Neutral, professional communication',
  },
  {
    value: 'direct',
    label: 'Direct / no-nonsense',
    description: 'Less filler and quicker movement to analysis or action',
  },
];

async function readPreferences(response: Response): Promise<AIInteractionPreferences> {
  const data = (await response.json()) as Partial<AIInteractionPreferences>;
  return {
    response_style: data.response_style ?? defaultPreferences.response_style,
    communication_style: data.communication_style ?? defaultPreferences.communication_style,
    clinical_lenses: data.clinical_lenses ?? [],
    default_clinical_lenses: data.default_clinical_lenses ?? [],
    clinical_user: data.clinical_user ?? false,
  };

}

export default function AIInteractionPreferencesTab({
  embedded = false,
  onSaved,
}: {
  embedded?: boolean;
  onSaved?: () => void;
}) {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const toggleLens = (field: 'clinical_lenses' | 'default_clinical_lenses', lens: string) => {
    setPreferences((current) => {
      const values = current[field].includes(lens)
        ? current[field].filter((value) => value !== lens)
        : [...current[field], lens];
      if (field === 'clinical_lenses') {
        return {
          ...current,
          clinical_lenses: values,
          default_clinical_lenses: current.default_clinical_lenses.filter((value) =>
            values.includes(value),
          ),
        };
      }
      return { ...current, [field]: values };
    });
  };

  useEffect(() => {
    let active = true;
    const loadPreferences = async () => {
      try {
        const response = await get('/api/user-ai-preferences');
        if (!response.ok) {
          throw new Error('Unable to load AI preferences');
        }
        const nextPreferences = await readPreferences(response);
        if (active) {
          setPreferences(nextPreferences);
        }
      } catch {
        if (active) {
          setError('Unable to load AI preferences. Please try again.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void loadPreferences();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const response = await patch('/api/user-ai-preferences', {
        response_style: preferences.response_style,
        communication_style: preferences.communication_style,
        clinical_lenses: preferences.clinical_lenses,
        default_clinical_lenses: preferences.default_clinical_lenses,
      });
      if (!response.ok) {
        throw new Error('Unable to save AI preferences');
      }
      setPreferences(await readPreferences(response));
      setSaved(true);
      onSaved?.();
    } catch {
      setError('Unable to save AI preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <SectionSkeleton message="Loading AI preferences..." />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        {!embedded && <Typography variant="h6">AI Preferences</Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Personalize how Kindredly communicates with you. These preferences do not change
          clinical facts, permissions, or documentation requirements.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">AI preferences saved.</Alert>}

      <Card>
        <CardContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">How should Kindredly respond to you?</FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
              Supportive ← Balanced → Challenging
            </Typography>
            <RadioGroup
              value={preferences.response_style}
              onChange={(event) =>
                setPreferences((current) => ({
                  ...current,
                  response_style: event.target.value as ResponseStyle,
                }))
              }
            >
              {responseOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">{option.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1, '.MuiRadio-root': { mt: 0.25 } }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {preferences.clinical_user && (
        <Card>
          <CardContent>
            <FormLabel component="legend">Clinical lens palette</FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Optional approaches Kindredly may consider. An empty selection is valid — notes use an Integrative orientation when nothing is set.
            </Typography>
            <Stack sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', mt: 1 }}>
              {clinicalLensOptions.map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  control={<Checkbox checked={preferences.clinical_lenses.includes(value)} onChange={() => toggleLens('clinical_lenses', value)} />}
                  label={label}
                />
              ))}
            </Stack>
            <FormLabel component="legend" sx={{ mt: 2 }}>My default clinical lenses</FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Defaults must be selected from your palette.
            </Typography>
            <Stack sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', mt: 1 }}>
              {clinicalLensOptions.map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  control={<Checkbox checked={preferences.default_clinical_lenses.includes(value)} disabled={!preferences.clinical_lenses.includes(value)} onChange={() => toggleLens('default_clinical_lenses', value)} />}
                  label={label}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">How should Kindredly communicate?</FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
              Warm & conversational ← Balanced → Direct / no-nonsense
            </Typography>
            <RadioGroup
              value={preferences.communication_style}
              onChange={(event) =>
                setPreferences((current) => ({
                  ...current,
                  communication_style: event.target.value as CommunicationStyle,
                }))
              }
            >
              {communicationOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">{option.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1, '.MuiRadio-root': { mt: 0.25 } }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        onClick={() => void handleSave()}
        disabled={saving}
        fullWidth
        sx={{ alignSelf: { sm: 'flex-start' }, width: { sm: 'auto' } }}
      >
        {saving ? 'Saving…' : 'Save AI preferences'}
      </Button>
    </Stack>
  );
}
